export type Brand<BaseType, BrandName> = BaseType & {
  readonly __brand: BrandName;
};

export type HexColor = Brand<string, 'HexColor'>;
export type RgbColor = Brand<string, 'RgbColor'>;
export type RgbaColor = Brand<string, 'RgbaColor'>;
export type TailwindClass = Brand<string, 'TailwindClass'>;
export type CssColor = HexColor | RgbColor | RgbaColor;

export const isHexColor = (value: string): value is HexColor => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
};

export const isRgbColor = (value: string): value is RgbColor => {
  return /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(value);
};

export const isRgbaColor = (value: string): value is RgbaColor => {
  return /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[0-1]?\.?\d*\)$/.test(value);
};

export const isTailwindClass = (value: string): value is TailwindClass => {
  return /^(bg-|text-|border-|shadow-|ring-)/.test(value);
};

export const createHexColor = (value: string): HexColor => {
  if (!isHexColor(value)) {
    throw new Error(`Invalid hex color: ${value}. Must be in format #RGB or #RRGGBB`);
  }
  return value as HexColor;
};

export const createRgbColor = (value: string): RgbColor => {
  if (!isRgbColor(value)) {
    throw new Error(`Invalid RGB color: ${value}. Must be in format rgb(r, g, b)`);
  }
  return value as RgbColor;
};

export const createRgbaColor = (value: string): RgbaColor => {
  if (!isRgbaColor(value)) {
    throw new Error(`Invalid RGBA color: ${value}. Must be in format rgba(r, g, b, a)`);
  }
  return value as RgbaColor;
};

export const createTailwindClass = (value: string): TailwindClass => {
  if (!isTailwindClass(value)) {
    throw new Error(`Invalid Tailwind class: ${value}. Must start with bg-, text-, border-, shadow-, or ring-`);
  }
  return value as TailwindClass;
};

export const normalizeHexColor = (color: HexColor): HexColor => {
  if (color.length === 4) {
    return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}` as HexColor;
  }
  return color;
};

export const getTailwindCategory = (className: TailwindClass): string => {
  if (className.startsWith('bg-')) return 'background';
  if (className.startsWith('text-')) return 'text';
  if (className.startsWith('border-')) return 'border';
  if (className.startsWith('shadow-')) return 'shadow';
  if (className.startsWith('ring-')) return 'ring';
  return 'unknown';
};

export interface ColorProps {
  backgroundColor?: CssColor;
  textColor?: CssColor;
  borderColor?: CssColor;
  tailwindClass?: TailwindClass;
}

export const convertToCssVariable = (color: CssColor, variableName: string): string => {
  return `var(--${variableName}, ${color})`;
};
